import { useMemo } from 'react'
import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query'
import { DocComment } from '@/types/comments'
import { Count } from '@/types/count'
import PaginatedResponse from '@/types/paginated-response'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import buildService from '../create-service'
import queryKeys from '../query-keys'
import { getList } from '../request-wrappers'

const service = buildService<DocComment>({
  pathCategory: 'document_comments',
})

export function useGetDocumentComments({
  filters,
  ...options
}: UseGetListOptions<DocComment> = {}) {
  const query = useGetList<DocComment>({
    filters,
    queryKey: queryKeys.documentComments.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    documentComments: query.allData,
    ...query,
  }
}

export function useCreateDocumentComment(
  options: UseCreateDetailOptions<DocComment> = {},
) {
  const mutation = useCreateDetail<DocComment>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createDocumentComment: mutation.mutate,
    createDocumentCommentAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export type UseDeleteDocumentCommentOptions = Omit<
  UseDeleteDetailOptions,
  'listQueryKey'
> & {
  /** The query key for the cached comment list to turn the deleted comment into a tombstone in. */
  listQueryKey?: QueryKey
}

/**
 * The API soft-deletes (returns 204, no body), so on success we can't read the
 * updated record back from the response. We optimistically rewrite the matching
 * cached item to its tombstone shape in onMutate — the UI updates instantly, and
 * sideEffectQueryKeys automatically invalidates the query so the cache is corrected from the
 * server whether the delete succeeds or fails.
 */
export function useDeleteDocumentComment({
  listQueryKey,
  sideEffectQueryKeys = [],
  onMutate,
  ...options
}: UseDeleteDocumentCommentOptions = {}) {
  const queryClient = useQueryClient()

  const mutation = useDeleteDetail<DocComment>({
    serviceFn: service.deleteDetail,
    onMutate: async (id, ...rest) => {
      if (listQueryKey) {
        await queryClient.cancelQueries({ queryKey: listQueryKey })
        queryClient.setQueryData<InfiniteData<PaginatedResponse<DocComment>>>(
          listQueryKey,
          (old: InfiniteData<PaginatedResponse<DocComment>> | undefined) =>
            old && {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                results: page.results.map((comment) =>
                  comment.id === id
                    ? {
                        ...comment,
                        body: null,
                        deleted_at: new Date().toISOString(),
                      }
                    : comment,
                ),
              })),
            },
        )
      }

      onMutate?.(id, ...rest)
    },
    ...options,
    sideEffectQueryKeys: [listQueryKey, ...sideEffectQueryKeys].filter(Boolean),
  })

  return {
    deleteDocumentComment: mutation.mutate,
    deleteDocumentCommentAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Get Document Comments Count
 * Used to get the count of active (non-deleted) comments on a document, via
 * the `~count` action rather than fetching the full list.
 */
export function useGetDocumentCommentsCount({
  filters,
  ...options
}: UseGetListOptions<Count> = {}) {
  const updatedFilters = useMemo(
    () => ({
      'api-consistency': 'strong',
      ...filters, // Allow api-consistency to be overridden
    }),
    [filters],
  )
  const query = useGetList<Count>({
    filters: updatedFilters,
    queryKey: queryKeys.documentComments.list({ filters: updatedFilters }, [
      'count',
    ]),
    serviceFn: (serviceFnOptions) =>
      getList<Count>({
        url: '/v2/pd/document_comments/~count',
        ...serviceFnOptions,
      }),
    ...options,
  })

  return {
    commentsCount: query.data?.pages[0]?.count,
    ...query,
  }
}
